import { timingFunctions } from "polished";
import * as React from "react";
import { Collapse, Form } from "react-bootstrap";
import styled, { css } from "styled-components";

import { FilterName, KeyValue, SvgSprite, useInitialFocus } from "@skv/core";

import useOutsideClick from "@skv/core/src/hooks/useOutsideClick";

const FilterHeadingBtn = styled.button.attrs({
  type: "button",
})<{ $isInactive: boolean }>`
  border: 0;
  background-color: transparent;
  line-height: 1;
  transition: color 150ms ${timingFunctions("easeOutCubic")};

  ${(p) => css`
    color: ${p.theme.lightColor}${p.$isInactive ? "4D" : "FF"};
  `}
`;

const FilterCheckboxLabel = styled.span<{ $isDisabled: boolean }>`
  position: relative;
  color: ${(p) => p.theme.gray900};
  top: ${1 / 16}rem;
  cursor: ${(p) => (p.$isDisabled ? "default" : "pointer")};

  &:hover {
    font-weight: ${(p) => p.$isDisabled === false && "bold"};
  }
`;

interface AdditionalFilterProps {
  heading: string;
  name: FilterName;
  data: KeyValue[];
  openFilter: FilterName | null;
  setOpenFilter: React.Dispatch<React.SetStateAction<FilterName | null>>;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabledCategory?: number;
  shouldFocus: boolean;
  selectedIds: number[];
}

export const AdditionalFilter = ({
  heading,
  name,
  data,
  openFilter,
  setOpenFilter,
  onChange,
  disabledCategory,
  shouldFocus,
  selectedIds,
}: AdditionalFilterProps) => {
  const type = React.useMemo(() => {
    switch (name) {
      case "category":
        return "categories";
      case "commitment":
        return "commitments";
      case "daytime":
        return "availabilities";
      case "suitablefor":
        return "suitabilities";
      case "typeofwork":
        return "work types";
      default:
        throw Error("Filter name is not defined.");
    }
  }, [name]);

  const isOpen = openFilter === name;

  const displayHeading =
    selectedIds.length === 0
      ? `${type === "suitabilities" ? "Any" : "All"} ${type}`
      : selectedIds.length === 1
      ? data.find((n) => n.id === selectedIds[0])?.name || heading
      : `${selectedIds.length} ${type}`;

  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const filterHeadingRef = useInitialFocus<HTMLButtonElement>({
    shouldFocus,
  });

  const closeDropdown = () => {
    if (openFilter === name) {
      setOpenFilter(null);
    }
  };

  useOutsideClick(dropdownRef, closeDropdown);

  const toggleDropdown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    isOpen ? setOpenFilter(null) : setOpenFilter(name);
  };

  return (
    <fieldset
      className="px-3 py-1"
      css={`
        flex: 1 1 33.3%;
        border-right: 1px solid rgba(255, 255, 255, 0.3);

        &:last-of-type {
          border-right: unset;
        }

        @media (min-width: ${(p) => p.theme.smBreakpoint}) {
          position: relative;

          &:first-of-type {
            border-left: 1px solid rgba(255, 255, 255, 0.3);
          }
          &:last-of-type {
            border-right: 1px solid rgba(255, 255, 255, 0.3);
          }
        }

        @media (min-width: ${(p) => p.theme.mdBreakpoint}) {
          flex: unset;
        }
      `}
    >
      <div className="d-flex flex-column flex-md-row align-items-md-center gap-md-2">
        <small
          id="filter-btn-heading"
          className="font-weight-bold text-truncate"
          css={`
            @media (min-width: ${(p) => p.theme.mdBreakpoint}) {
              line-height: 1;
              overflow: unset;
            }
          `}
        >
          {heading}
          <span className="d-none d-md-inline">:</span>
        </small>
        <FilterHeadingBtn
          ref={filterHeadingRef}
          $isInactive={openFilter !== null && openFilter !== name}
          className="position-relative d-flex text-truncate small focus-light p-1 mx-n1"
          aria-label={`toggle ${heading}`}
          aria-expanded={isOpen ? "true" : "false"}
          aria-describedby="filter-btn-heading"
          onClick={toggleDropdown}
        >
          <span
            className="text-truncate"
            css={`
              max-width: calc(100% - 1rem);
            `}
          >
            {displayHeading}
          </span>

          <SvgSprite
            id="glyph:arrow-down"
            width={14}
            height={14}
            aria-label={`Select ${heading}`}
            className="flex-shrink-0"
            style={{
              marginLeft: "0.375rem",
              transition: `transform 250ms ${timingFunctions("easeOutCubic")}`,
              transform: isOpen
                ? "rotate3d(1,0,0,180deg)"
                : "rotate3d(0,0,0,0deg)",
            }}
          />
        </FilterHeadingBtn>

        <Collapse in={isOpen} mountOnEnter unmountOnExit timeout={0}>
          <div
            ref={dropdownRef}
            className="position-absolute bg-white shadow"
            css={`
              left: 0;
              top: 100%;
              z-index: 40;
              max-height: 400px;
              overflow: visible auto;
              width: 100%;

              @media (min-width: ${(p) => p.theme.smBreakpoint}) {
                top: calc(100% + 0.5rem);
                width: 300px;
              }
            `}
          >
            <div className="p-3">
              {data.map((filter) => {
                const isDisabled =
                  name === "category" && filter.id === disabledCategory;
                return (
                  <Form.Group key={`${name}-${filter.id}`} className="mb-2">
                    <Form.Check
                      custom
                      name={name}
                      type="checkbox"
                      id={`filter-checkbox-${name}-${filter.id}`}
                      label={
                        <FilterCheckboxLabel
                          $isDisabled={isDisabled}
                          className="small"
                        >
                          {filter.name}
                        </FilterCheckboxLabel>
                      }
                      onChange={(e) => {
                        onChange(e);
                        setOpenFilter(null);
                      }}
                      value={filter.id}
                      disabled={isDisabled}
                      checked={
                        selectedIds ? selectedIds.includes(filter.id) : false
                      }
                    />
                  </Form.Group>
                );
              })}
            </div>
          </div>
        </Collapse>
      </div>
    </fieldset>
  );
};
